/**
 * Landscape gate for touch devices.
 *
 * The farm is a side-on world with a HUD along the top and thumb controls in the
 * bottom corners. In portrait there is nowhere for any of that to go: the joystick
 * and the action button end up under the hotbar, and the visible slice of world is
 * a few tiles wide. Rather than trying to make a second, worse layout work, the
 * game asks to be turned — the same call every landscape mobile game makes.
 *
 * Portrait is a hard stop, not a suggestion: the farm is unplayable in it. So the
 * prompt never offers a way past — it offers ways to ROTATE.
 *
 * Three mechanisms, because none is reliable alone:
 *  - The Screen Orientation API locks to landscape where it is allowed (installed
 *    PWA / fullscreen on Android). It throws or is absent everywhere else — most
 *    notably every iOS browser — so the failure is swallowed.
 *  - A full-screen prompt covers the game whenever the viewport is portrait. This
 *    is the one that actually does the work on iOS.
 *
 * Desktop is untouched: a narrow desktop window is a small window, not a phone
 * held the wrong way, and blocking it would be absurd.
 */

import { isTouchDevice } from '../map/joystick';

export interface OrientationGate {
  destroy(): void;
}

/**
 * The one gate, owned by the ROUTER rather than by a view.
 *
 * It used to be created inside `mountGame`, which left a hole exactly where a new
 * player meets the game: after registering, the router renders the CHARACTER
 * CREATOR instead of the game, so the first screen a phone ever showed had no
 * gate on it at all. Owner: "not allows you play vertically, only horizontal …
 * after login/reg/forgot password page."
 *
 * One owner also means one gate. Mounting per view invited two of them alive at
 * once during a transition, each with its own resize listener.
 *
 * The auth screens are deliberately NOT gated. A sign-in form is a column of two
 * fields and a button — it works perfectly upright, and demanding a rotation
 * before someone can even type their password is a worse first impression than
 * the portrait layout would ever be. The gate begins where the GAME begins, which
 * is what the owner asked for.
 */
let gate: OrientationGate | null = null;

export function setOrientationGate(active: boolean): void {
  if (active === !!gate) return;          // idempotent: the router calls this on every render
  if (active) { gate = createOrientationGate(document.body); return; }
  gate?.destroy();
  gate = null;
}

/** Test seam: drop the gate without going through the router. */
export function __resetOrientationGate(): void {
  gate?.destroy();
  gate = null;
}

/** Portrait as far as the game is concerned: taller than it is wide. */
function isPortrait(): boolean {
  return window.innerHeight > window.innerWidth;
}

/**
 * Ask the browser to lock to landscape. Resolves to false everywhere the API is
 * unavailable or refuses (iOS Safari always, and any browser outside fullscreen),
 * which is why the prompt below is not optional.
 */
export async function tryLockLandscape(): Promise<boolean> {
  const orientation = screen.orientation as (ScreenOrientation & {
    lock?: (o: string) => Promise<void>;
  }) | undefined;
  if (!orientation?.lock) return false;
  try {
    await orientation.lock('landscape');
    return true;
  } catch {
    return false;   // not fullscreen, not permitted, or not supported
  }
}

/**
 * Mount the rotate prompt. No-op on anything that isn't a touch device.
 *
 * @param host element the prompt is appended to (position: relative or fixed).
 */
export function createOrientationGate(host: HTMLElement): OrientationGate {
  if (!isTouchDevice()) return { destroy() { /* desktop: nothing to gate */ } };

  void tryLockLandscape();

  const overlay = document.createElement('div');
  overlay.setAttribute('role', 'alertdialog');
  overlay.setAttribute('aria-label', 'Rotate your device to landscape');
  overlay.style.cssText = `position:fixed;inset:0;z-index:9999;display:none;
    align-items:center;justify-content:center;padding:24px;
    background:#140f0a url('/ui/login_bg.webp') center center / cover no-repeat;
    image-rendering:pixelated`;

  // A wooden signboard, same language as the auth screens: dark plank, warm
  // stained border, chunky bevel. Built in CSS rather than as an image so it
  // scales to any phone and needs no second asset.
  const panel = document.createElement('div');
  panel.style.cssText = `display:flex;flex-direction:column;align-items:center;gap:14px;
    max-width:min(420px, 88vw);padding:26px 30px;text-align:center;
    background:linear-gradient(180deg,#3a2e24,#241d18);
    border:4px solid #6b4f34;border-radius:12px;
    box-shadow:inset 0 -4px 0 rgba(0,0,0,.35), inset 0 3px 0 rgba(255,215,120,.14),
      0 14px 30px rgba(0,0,0,.55);
    color:#e9d6bd;font-family:"Terraboma","CuteFantasy",var(--font-system,sans-serif)`;

  // Pixel-art rotate icon (generated, Cute-Fantasy palette). `pixelated` keeps the
  // 64px source chunky instead of blurring it up to display size.
  const icon = document.createElement('img');
  icon.src = '/ui/rotate_phone.png';
  icon.alt = '';
  icon.setAttribute('aria-hidden', 'true');
  icon.style.cssText = `width:96px;height:96px;image-rendering:pixelated;
    filter:drop-shadow(0 3px 0 rgba(0,0,0,.45));
    animation:tb-rotate-hint 2.4s ease-in-out infinite`;

  const title = document.createElement('div');
  title.textContent = 'Turn your device';
  title.style.cssText = 'font-size:24px;letter-spacing:.03em;color:#ffd97a;text-shadow:0 2px 0 rgba(0,0,0,.5)';

  const body = document.createElement('div');
  body.textContent = 'Terraboma is played sideways — turn your phone to start farming.';
  body.style.cssText = 'font-size:14px;line-height:1.5;color:#cbb79b;max-width:30ch';

  // The icon already reads as a turning phone; the animation nudges it rather than
  // spinning it a full quarter-turn, which at 96px looked like a glitch.
  const style = document.createElement('style');
  style.textContent = `@keyframes tb-rotate-hint{
    0%,40%{transform:rotate(-12deg)}70%,100%{transform:rotate(12deg)}}
    @media (prefers-reduced-motion: reduce){
      #tb-orientation-icon{animation:none !important}}`;
  icon.id = 'tb-orientation-icon';

  // NO "play anyway" bypass: the farm genuinely does not work in portrait — a
  // side-on world with a HUD across the top and thumb controls in the bottom
  // corners has nowhere to put any of that in a tall, narrow viewport, and the
  // visible slice of world is a few tiles wide. Letting a player through would
  // hand them a broken game and a bad first impression rather than a 3-second
  // rotation.
  //
  // But the gate cannot simply be inescapable either. Its only natural exit is the
  // viewport turning landscape, and a phone with rotation lock ON never turns — so
  // an unhelped player is shut out entirely. The fix is to help them ROTATE rather
  // than to let them past:
  //  - the button asks the browser to do it (fullscreen + an orientation lock is
  //    the only way a page can rotate itself, and it works on Android/installed PWAs)
  //  - iOS refuses both, so the hint names the actual remedy: the Control-Centre
  //    rotation lock. Naming the switch is the difference between "turn your phone"
  //    (which they already tried) and something they can act on.
  const rotate = document.createElement('button');
  rotate.type = 'button';
  rotate.textContent = 'Rotate for me';
  rotate.style.cssText = `margin-top:2px;padding:10px 18px;cursor:pointer;min-height:44px;
    background:#4a3a2a;color:#ffd97a;border:3px solid #6b4f34;border-radius:8px;
    font-family:inherit;font-size:14px;
    box-shadow:inset 0 -3px 0 rgba(0,0,0,.35), inset 0 2px 0 rgba(255,215,120,.12)`;

  const hint = document.createElement('div');
  hint.style.cssText = 'font-size:12px;line-height:1.5;color:#a08d76;max-width:32ch';
  hint.textContent = 'Still upright? Turn off rotation lock — iPhone: swipe down for '
    + 'Control Centre and tap the padlock-with-arrow. Android: swipe down and tap Auto-rotate.';

  rotate.addEventListener('click', () => {
    // Fullscreen first: every browser that allows an orientation lock requires it.
    const el = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> };
    const goFull = el.requestFullscreen?.bind(el) ?? el.webkitRequestFullscreen?.bind(el);
    void Promise.resolve(goFull?.())
      .then(() => tryLockLandscape())
      .then((locked) => { if (!locked) hint.style.color = '#ffd97a'; })   // highlight the manual remedy
      .catch(() => { hint.style.color = '#ffd97a'; });
  });

  panel.append(icon, title, body, rotate, hint);
  overlay.append(panel);
  host.appendChild(overlay);
  document.head.appendChild(style);

  const sync = () => { overlay.style.display = isPortrait() ? 'flex' : 'none'; };
  sync();

  window.addEventListener('resize', sync);
  window.addEventListener('orientationchange', sync);

  return {
    destroy() {
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
      overlay.remove();
      style.remove();
    },
  };
}
